<template>
    <div
        :key="widgetGroup.WidgetGroupID"
        class="flex flex-col h-full w-full pb-2"
        :style="gridLayoutStyle"
    >
        <div
            :class="getClass"
            :id="gridId"
            :key="gridId"
            ref="widgetListContainer"
        >
            <div
                :data-id="widget.WidgetID"
                @mousedown="onMousedown"
                class="grid-stack-item overflow-hidden"
                :id="widget.WidgetID"
                v-bind="gridStackAttributes(widget)"
                v-for="widget in widgets"
                :key="widget.WidgetID"
            >
                <WidgetErrorBoundary>
                    <Widget
                        :editable="editable"
                        :inViewById="inViewById"
                        :key="widget.WidgetID"
                        :widget="widget"
                        :show-widget-content="widgetIdsToShow.includes(widget.WidgetID)"
                        @remove-item="removeWidget"
                        @update-item="updateWidget"
                        v-on="$listeners"
                        :previewMode="previewMode"
                    />
                </WidgetErrorBoundary>
            </div>
        </div>
        <div
            :key="`no-data-${widgetGroup.WidgetGroupID}`"
            class="w-full flex flex-col items-center mt-20"
            v-if="widgets.length === 0"
        >
            <div class="flex flex-col items-center">
                <icon-no-data-cry class="h-61 w-61" />
                <p class="main-text-color max-w-lg text-center">
                    {{ $t('dashboard.widgetGroupNoData') }}
                </p>
            </div>
        </div>
    </div>
</template>
<script>
    import get from 'lodash/get'
    import debounce from 'lodash/debounce'
    import bus from '@/event-bus/EventBus'
    import Widget from './Widget'
    import WidgetErrorBoundary from '@/components/WidgetErrorBoundary'

    export default {
        components: {
            Widget,
            WidgetErrorBoundary
        },
        data() {
            return {
                grid: null,
                inViewById: {},
                loading: false,
                gridLayoutStyle: {},
                widgetIdsToShow: []
            }
        },
        props: {
            widgetGroup: {
                type: Object,
                default: null,
            },
            editable: {
                type: Boolean,
                default: false,
            },
            widgets: {
                type: Array,
                default: () => [],
            },
            previewMode: {
                type: Boolean,
                default: false
            }
        },
        computed: {
            getClass() {
                return `grid-stack grid-stack-instance-${this.widgetGroup.WidgetGroupID}`
            },
            enabledRTL() {
                const currentLanguage = this.$store.state.lang.language
                return currentLanguage !== 'en'
            },
            gridId() {
                return `grid-stack-${this.widgetGroup.WidgetGroupID}`
            },
            widgetsAndWidgetsIDsToShow() {
                return this.widgets.map(({ WidgetID, loadedFromCache }) => `${WidgetID}_${loadedFromCache}|${this.widgetIdsToShow}`).join('')
            },
        },
        methods: {
            gridStackAttributes(widget) {
                return {
                    id: get(widget, 'WidgetID', '999'),
                    'data-gs-id': get(widget, 'WidgetID', '999'),
                    'data-gs-x': get(widget, 'WidgetLayout.GridLayout.x', 0),
                    'data-gs-y': get(widget, 'WidgetLayout.GridLayout.y', 2),
                    'data-gs-width': get(widget, 'WidgetLayout.GridLayout.width', 64),
                    'data-gs-height': get(widget, 'WidgetLayout.GridLayout.height', 4)
                }
            },
            addWidgetsToGroup(widget) {
                const gridStackContainer = this.$refs.widgetListContainer
                if (!gridStackContainer) {
                    return
                }
                const rowCount = gridStackContainer.getAttribute('data-gs-current-row')

                let GridLayout = {
                    x: 0,
                    y: Number(rowCount),
                    width: 12,
                    height: 2,
                }

                widget['WidgetLayout'] = {
                    ...widget['WidgetLayout'],
                    GridLayout,
                }

                let objectToEmit = {
                    widgets: [widget],
                    group: this.widgetGroup,
                }

                this.$emit('add-widgets-to-group', objectToEmit)
            },
            removeWidget(widget) {
                this.grid.removeWidget(`#${widget.WidgetID}`)
                this.$emit('remove-widget', { 'widget': widget, 'group': this.widgetGroup })
            },
            updateWidget(widget, silence) {
                this.$emit('update-widget', { 'widget': widget, 'group': this.widgetGroup, silence })
            },
            findMatchingGrid() {
                const matchingGridIndex = window.grids.findIndex(grid => grid.el.id === this.gridId)

                return {
                    grid: window.grids[matchingGridIndex],
                    index: matchingGridIndex,
                }
            },
            nextTick() {
                return new Promise((resolve) => {
                    this.$nextTick(resolve)
                })
            },
            async initWindowGrid() {
                const gridOptions = {
                    acceptWidgets: true,
                    column: 64
                }

                this.grid = window.GridStack.init(gridOptions, this.$refs.widgetListContainer)

                if (!this.grid) {
                    return
                }

                this.grid.movable('.grid-stack-item', this.editable)
                this.grid.resizable('.grid-stack-item', this.editable)
                window.grids.push(this.grid)

                this.resizeEventEmitter()
                this.onDragStartEvent()
                this.initIntersectionObserver()
            },
            resizeEventEmitter() {
                this.grid.on('gsresizestop', function (event, element) {
                    const widgetID = element.getAttribute('data-gs-id')
                    bus.$emit('widget-resized', widgetID)
                })
            },
            tryDestroyGrid() {
                const { grid, index } = this.findMatchingGrid()
                if (!grid) {
                    return
                }
                grid.destroy()
                window.grids.splice(index, 1)
            },
            async addWidgetsToGrid(widgets) {
                await this.$nextTick()

                widgets.forEach((widget) => {
                    this.inViewById[widget.WidgetID] = true
                    const isPresent = this.widgets.find(w => w.WidgetID === widget.WidgetID)

                    if (!isPresent) {
                        return
                    }

                    this.grid.makeWidget(`#${widget.WidgetID}`)
                })
            },
            onDragStartEvent() {
                this.grid.on('dragstart', () => {
                    this.onMousedown()
                })
            },
            onMousedown() {
                let preventMovable = !!(this.isWidgetModalOpen() || !this.editable)
                this.grid.movable('.grid-stack-item', !preventMovable)
            },
            isWidgetModalOpen() {
                let bodyElement = document.body
                if (bodyElement.classList.length) {
                    return false
                }
                return bodyElement.classList.contains('el-popup-parent--hidden')
            },
            initIntersectionObserver() {
                const sections = document.querySelectorAll('.grid-stack-item')

                const options = {
                    root: null,
                    threshold: 0.1,
                    rootMargin: '200px',
                }
                let inViewById = {}
                let callback = (entries, observer) => {
                    entries.forEach((entry) => {
                        let id = entry.target.dataset.id
                        if (!entry.isIntersecting) {
                            inViewById[id] = entry.isIntersecting
                        } else {
                            inViewById[id] = entry.isIntersecting
                            observer.unobserve(entry.target)
                        }
                    })
                    this.inViewById = inViewById
                }

                const observer = new IntersectionObserver(callback, options)

                sections.forEach((section) => {
                    observer.observe(section)
                })

                this.observer = observer
            },
            makeGridLayout () {
                if (!this.editable || !this.$el) {
                    return
                }
                const clientWidth = this.$el.clientWidth
                const gridGap = (clientWidth * 1.5625) / 100
                const rounded = Math.round((gridGap + Number.EPSILON) * 100) / 100
                return {
                    'background-image': 'radial-gradient(circle, var(--primary-color) 1px, transparent 0)',
                    'background-size': `${rounded}px 40px`,
                    'background-position': `${(rounded / 2) - 9}px 0`
                }
            },
            resizeWindowListener () {
                window.addEventListener('resize', this.setGridLayoutStyle)
            },
            setGridLayoutStyle () {
                this.gridLayoutStyle = this.makeGridLayout()
            },
            showNextWidget () {
                const sortedWidgetsToShow = [ ...this.widgets ]
                    .filter(({ loadedFromCache, WidgetID }) => !loadedFromCache && !this.widgetIdsToShow.includes(WidgetID))
                    .sort((a, b) => {
                        const aWidgetLayout = a.WidgetLayout || {}
                        const bWidgetLayout = b.WidgetLayout || {}

                        const aGrid = aWidgetLayout.GridLayout || { y: 0 }
                        const bGrid = bWidgetLayout.GridLayout || { y: 0 }

                        return aGrid.y - bGrid.y
                    })

                if (sortedWidgetsToShow.length > 0) {
                    this.widgetIdsToShow.push(sortedWidgetsToShow[0].WidgetID)
                }
            },
            onAddedWidgetsHandler (widgets) {
                return this.addWidgetsToGrid(widgets)
            }
        },
        async mounted() {
            await this.initWindowGrid()

            bus.$on('added-widgets', this.onAddedWidgetsHandler)

            this.resizeWindowListener()
        },
        watch: {
            editable: {
                handler(state) {
                    this.grid.movable('.grid-stack-item', state)
                    this.grid.resizable('.grid-stack-item', state)
                    this.setGridLayoutStyle()
                },
            },
            enabledRTL: {
                handler(state) {
                    this.grid.rtl = state
                },
            },
            widgetsAndWidgetsIDsToShow: {
                immediate: true,
                handler: debounce(
                    function () {
                        this.showNextWidget()
                    },
                    300
                )
            },
        },
        beforeDestroy() {
            this.tryDestroyGrid()

            if (this.observer) {
                this.observer.disconnect()
            }

            window.removeEventListener('resize', this.setGridLayoutStyle)

            bus.$off('added-widgets', this.onAddedWidgetsHandler)
        }
    }
</script>
